<template lang="pug">
  //- fills all available space - needs to be contained in a positioned element
  .video-embed(ref='container')
    .video-embed__aspect-ratio(
      :style='aspectRatioStyle'
    )
      iframe.video-embed__iframe(
        v-if="video"
        ref='videoFrame',
        :id='videoIframeId',
        v-bind='backgroundVideoAttrs',
      )
</template>

<script>
import { embedVideoIframeAttributes } from 'scripts/helpers/util.js';

export default {
  name: 'VideoEmbed',
  props: {
    video: String,
    loopStart: {
      type: Number,
      default: 0
    },
    loopEnd: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      containerWidth: 800,
      containerHeight: 400,
      youtubePlayer: null,
      vimeoPlayer: null,
      videoSdkInterval: null
    }
  },
  computed: {
    backgroundVideoAttrs () {
      return embedVideoIframeAttributes(this.video, { background: true });
    },
    aspectRatioStyle () {
      const heightRatio = 0.5625;
      const widthRatio = 1 / heightRatio;

      if (this.containerHeight / this.containerWidth > heightRatio) {
        // The video container is now too tall and black bars will appear on the top and bottom
        // the video must now be sized according to the height of its container
        return {
          width: (this.containerHeight * widthRatio) + 'px',
          paddingBottom: (widthRatio * 100) + '%'
        };
      } else {
        // The video container is wide enough that the video can naturally resize to fill its container
        return {
          width: '100%',
          paddingBottom: (heightRatio * 100) + '%'
        };
      }
    },
    videoIframeId () {
      // _uid is a private Vue component instance variable
      // technically it should only be used internally by Vue
      return 'video_' + this._uid;
    }
  },
  mounted () {
    window.addEventListener('resize', this.getDimensions);
    this.getDimensions();

    // Ensure both YouTube and Vimeo libraries are loaded on page
    this.videoSdkInterval = setInterval(() => {
      if (window.YT && window.YT.loaded === 1 && window.Vimeo) {
        clearInterval(this.videoSdkInterval);
        this.videoSdkInit()
      }
    }, 100)
  },
  beforeDestroy () {
    clearInterval(this.youtubeInterval)
    clearInterval(this.vimeoInterval)
    clearInterval(this.videoSdkInterval)

    window.removeEventListener('resize', this.getDimensions);
  },
  methods: {
    getDimensions () {
      this.containerWidth = this.$refs.container.clientWidth;
      this.containerHeight = this.$refs.container.clientHeight;
    },
    onYoutubeVideoReady (event) {
      event.target.mute();
      event.target.seekTo(this.loopStart, true)

      this.youtubeInterval = setInterval(() => {
        event.target.seekTo(this.loopStart, true )
      }, this.getDuration(event.target.getDuration()) * 1000)
    },
    getDuration (fullDuration) {
      let endpoint = this.loopEnd > 0 ? this.loopEnd : fullDuration
      let duration = fullDuration - this.loopStart - (fullDuration - endpoint)
      // if the duration is zero or negative we just default to the full duration
      if (duration < 0) {
        this.loopStart = 0;
        return fullDuration
      }
      return duration
    },
    videoSdkInit () {
      // If this is a YouTube embed, we have to mute it manually using the API
      // since there is no automatic background/mute attribute
      if (this.video.includes('youtube.com')) {
        this.youtubePlayer = new window.YT.Player(this.videoIframeId, {
          events: {
            onReady: this.onYoutubeVideoReady,
            onStateChange: this.onYoutubeVideoStateChange
          }
        });
      }

      // If this is a Vimeo embed, we initialize the Vimeo player
      if (this.video.includes('vimeo.com')) {
        const iframe = this.$refs.videoFrame;
        this.vimeoPlayer = new window.Vimeo.Player(iframe);

        this.vimeoPlayer.getDuration().then((fullDuration) => {
          this.vimeoPlayer.setCurrentTime(this.loopStart);

          this.vimeoInterval = setInterval(() => {
            this.vimeoPlayer.setCurrentTime(this.loopStart)
          }, this.getDuration(fullDuration) * 1000)
        });
      }
    }
  }
}
</script>

<style scoped lang="scss">
  .video-embed {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;

    &__aspect-ratio {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &__iframe {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;

      pointer-events: none;
    }
  }
</style>
